import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { RotateCcw, Loader2, Download, Smartphone, Search, Check } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useOrganization } from '@/contexts/organization-context'
import { optimizerService, AppMetrics } from '@/services/optimizer'
import { toast } from 'sonner'
import { useParams } from 'react-router-dom'
import { fitRetentionCurve, RetentionDataPoint } from '@/utils/retention-curve'

interface KPIValues {
  monthlyUASpend: number
  monthlyInstalls: number
  cpi: number
  day1Retention: number
  day7Retention: number
  day30Retention: number
  effectiveDAU: number // New: for retention rates card
  userLTV: number // New: replaces avgRevenuePerDailyUser
  eCPM: number // New: effective cost per mille for monetization simulation
  adImpressionRate: number
  monthlyAdRevenue: number // New: replaces iapConversionRate
  monthlyIAPRevenue: number // New: replaces avgIAPValue
  monthlyTotalRevenue: number // New: consolidated revenue slider
  projectionDays: number
}

// Helper interface for calculated metrics
interface CalculatedMetrics {
  totalRevenue: number
  totalSpend: number
  arpdau: number
  eCPM: number
  effectiveDAU: number
  isDAUEstimated: boolean
  dailyAdImpressions: number
}

// Retention curve fitting - piecewise exponential segments
const calculateRetentionCurveParams = (d1: number, d7: number, d30: number): { k1: number; k7: number; k30: number } => {
  // Fit three separate exponential curves:
  // - k1: D0 to D7 (using D1 and D7)
  // - k7: D7 to D30 (using D7 and D30)
  // - k30: D30+ (using D30 decay)

  // Convert to decimals
  const r1 = d1 / 100
  const r7 = d7 / 100
  const r30 = d30 / 100

  // Segment 1 (D0-D7): Handle edge cases for this segment only
  let k1: number
  if (d1 <= 0 || d7 <= 0) {
    k1 = 100 // Very high decay rate = essentially 0 retention
  } else {
    // Fit curve between D1 and D7
    // We want: r1 = e^(-k1*1) and r7 = e^(-k1*7)
    // Solving: k1 = ln(r1/r7) / (7-1)
    k1 = Math.log(r1 / r7) / 6
  }

  // Segment 2 (D7-D30): Handle edge cases for this segment only
  let k7: number
  if (d7 <= 0 || d30 <= 0) {
    k7 = 100 // Very high decay rate = essentially 0 retention
  } else {
    // Fit curve between D7 and D30
    // We want: r7 = e^(-k7*7) and r30 = e^(-k7*30)
    // Solving: k7 = ln(r7/r30) / (30-7)
    k7 = Math.log(r7 / r30) / 23
  }

  // Segment 3 (D30+): Continue same decay rate as segment 2 for continuity
  const k30 = k7

  return { k1, k7, k30 }
}

// Calculate retention for any day using piecewise exponential curves
const calculateRetentionForDay = (
  day: number,
  params: { k1: number; k7: number; k30: number },
  d1: number,
  d7: number,
  d30: number
): number => {
  if (day === 0) return 1.0

  const r1 = d1 / 100
  const r7 = d7 / 100
  const r30 = d30 / 100

  // Segment 1: D0 to D7
  if (day <= 7) {
    // retention(t) = r1 * e^(-k1 * (t-1))
    // At t=1: r1 * e^0 = r1 ✓
    // At t=7: r1 * e^(-k1 * 6) should equal r7
    return r1 * Math.exp(-params.k1 * (day - 1))
  }

  // Segment 2: D7 to D30
  if (day <= 30) {
    // retention(t) = r7 * e^(-k7 * (t-7))
    // At t=7: r7 * e^0 = r7 ✓
    // At t=30: r7 * e^(-k7 * 23) should equal r30
    return r7 * Math.exp(-params.k7 * (day - 7))
  }

  // Segment 3: D30+
  // retention(t) = r30 * e^(-k30 * (t-30))
  return r30 * Math.exp(-params.k30 * (day - 30))
}

// Calculation functions
const getEffectiveDAU = (
  realDAU: number,
  ad_impressions: number,
  ad_revenue: number,
  eCPM: number
): { effectiveDAU: number; isDAUEstimated: boolean } => {
  if (realDAU > 0) {
    // DAU from API is sum over 30-day period, convert to daily average
    // Round up to nearest integer (can't have fractional users)
    return { effectiveDAU: Math.ceil(realDAU / 30), isDAUEstimated: false }
  }
  
  // Estimate DAU from impressions using eCPM and ad revenue
  if (ad_impressions > 0 && ad_revenue > 0 && eCPM > 0) {
    // Calculate impressions per DAU from eCPM and revenue data
    // eCPM = ad_revenue / (ad_impressions / 1000)
    // So: estimated impressions per DAU = ad_impressions / (estimated_DAU * 30)
    // We can derive DAU from: DAU = ad_revenue / (eCPM * daily_impressions_per_user / 1000 * 30)
    const dailyImpressions = ad_impressions / 30
    const estimatedImpressionPerDAU = (eCPM * dailyImpressions / 1000) / (ad_revenue / 30)
    const estimatedDAU = Math.round(dailyImpressions / Math.max(1, estimatedImpressionPerDAU))
    return { effectiveDAU: Math.max(1, estimatedDAU), isDAUEstimated: true }
  }
  
  // Fallback: use ad impressions with default eCPM assumption
  if (ad_impressions > 0) {
    const fallbackImpressionsPerDAU = 10 // Default fallback
    const estimatedDAU = Math.round((ad_impressions / 30) / fallbackImpressionsPerDAU)
    return { effectiveDAU: estimatedDAU, isDAUEstimated: true }
  }
  
  return { effectiveDAU: 0, isDAUEstimated: false }
}

const calculateDerivedMetrics = (
  metrics: AppMetrics, 
  userECPM: number
): CalculatedMetrics => {
  const { effectiveDAU, isDAUEstimated } = getEffectiveDAU(
    metrics.dau || 0, 
    metrics.ad_impressions || 0,
    metrics.ad_revenue || 0,
    userECPM
  )
  
  const totalRevenue = (metrics.ad_revenue || 0) + (metrics.iap_revenue || 0)
  const totalSpend = metrics.ua_spent || 0
  const arpdau = effectiveDAU > 0 ? totalRevenue / effectiveDAU / 30 : 0 // Daily ARPDAU
  const eCPM = (metrics.ad_impressions || 0) > 0 ? 
    (metrics.ad_revenue || 0) / ((metrics.ad_impressions || 0) / 1000) : 0
  const dailyAdImpressions = (metrics.ad_impressions || 0) / 30
  
  return {
    totalRevenue,
    totalSpend,
    arpdau,
    eCPM,
    effectiveDAU,
    isDAUEstimated,
    dailyAdImpressions
  }
}

/**
 * Calculate steady-state DAU from daily install rate and retention curve
 *
 * Steady state is reached when daily new users equals daily churned users.
 * DAU = sum of all active cohorts at equilibrium.
 */
const calculateSteadyStateDAU = (
  dailyInstalls: number,
  retentionD1: number,
  retentionD7: number,
  retentionD30: number
): number => {
  const d1Retention = retentionD1 / 100
  const d7Retention = retentionD7 / 100
  const d30Retention = retentionD30 / 100

  let steadyStateDAU = 0
  const maxDays = 365 // Calculate over 365 days (beyond this, contribution is negligible)

  // Day 0: All newly acquired users are active
  steadyStateDAU += dailyInstalls * 1.0

  // Days 1-365: Add retained users from each cohort
  for (let daysSinceInstall = 1; daysSinceInstall <= maxDays; daysSinceInstall++) {
    let retentionRate = 1

    if (daysSinceInstall === 1) {
      retentionRate = d1Retention
    } else if (daysSinceInstall <= 7) {
      const ratio = (daysSinceInstall - 1) / 6
      retentionRate = d1Retention * (1 - ratio) + d7Retention * ratio
    } else if (daysSinceInstall <= 30) {
      const ratio = (daysSinceInstall - 7) / 23
      retentionRate = d7Retention * (1 - ratio) + d30Retention * ratio
    } else {
      const monthsAfter30 = (daysSinceInstall - 30) / 30
      retentionRate = d30Retention * Math.pow(0.9, monthsAfter30)
    }

    steadyStateDAU += dailyInstalls * retentionRate
  }

  return steadyStateDAU
}

const estimateLTVFromData = (
  arpdau: number,
  retentionD1: number,
  retentionD7: number,
  retentionD30: number,
  _timeHorizonMonths: number // Kept for backward compatibility, but not used
): number => {
  // More sophisticated LTV estimation based on retention curve
  const d1Retention = retentionD1 / 100
  const d7Retention = retentionD7 / 100
  const d30Retention = retentionD30 / 100

  // Calculate cumulative revenue over FIXED 365-day period (LTV should be independent of projection horizon)
  let totalLTV = 0
  const maxDays = 365 // Fixed lifetime period

  for (let day = 1; day <= maxDays; day++) {
    let retentionRate = 1

    if (day === 1) {
      retentionRate = d1Retention
    } else if (day <= 7) {
      // Linear interpolation between D1 and D7
      const ratio = (day - 1) / 6
      retentionRate = d1Retention * (1 - ratio) + d7Retention * ratio
    } else if (day <= 30) {
      // Linear interpolation between D7 and D30
      const ratio = (day - 7) / 23
      retentionRate = d7Retention * (1 - ratio) + d30Retention * ratio
    } else {
      // Exponential decay after D30
      const monthsAfter30 = (day - 30) / 30
      retentionRate = d30Retention * Math.pow(0.9, monthsAfter30)
    }

    totalLTV += arpdau * retentionRate
  }

  return totalLTV // Return actual calculated value without artificial bounds
}


export function EdgePage() {
  const { organizations } = useOrganization()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [apps, setApps] = useState<AppMetrics[]>([])
  const [loadingApps, setLoadingApps] = useState(false)
  const [selectedApp, setSelectedApp] = useState<string>('All Apps')
  const [appSelectorOpen, setAppSelectorOpen] = useState(false)
  const [appSearch, setAppSearch] = useState('')
  const isLoadingRef = useRef(false)
  const [retentionCache, setRetentionCache] = useState<Map<string, RetentionDataPoint[]>>(new Map())
  const [_loadingRetention, setLoadingRetention] = useState(false)
  const [kpis, setKPIs] = useState<KPIValues>({
    monthlyUASpend: 0,
    monthlyInstalls: 0,
    cpi: 0,
    day1Retention: 40,
    day7Retention: 20,
    day30Retention: 10,
    effectiveDAU: 0,
    userLTV: 0,
    eCPM: 0,
    adImpressionRate: 0,
    monthlyAdRevenue: 0,
    monthlyIAPRevenue: 0,
    monthlyTotalRevenue: 0, // Will be populated from DB (ad + iap revenue)
    projectionDays: 180,
  })

  const [cashflowData, setCashflowData] = useState<any[]>([])
  const [_breakEvenMonth, setBreakEvenMonth] = useState<number>(-1)
  const [isPositive, setIsPositive] = useState(true)
  const [calculatedMetrics, setCalculatedMetrics] = useState<CalculatedMetrics | null>(null)

  // Track which metrics are using default values (no data from API)
  const [_metricsUsingDefaults, _setMetricsUsingDefaults] = useState<Set<string>>(new Set())

  // 365-day analysis data for consistent messaging
  const [analysis365, setAnalysis365] = useState<{
    profit: number
    roi: number
    breakEvenDay: number
    lastProfitableDay: number
    dailyBurnRate: number
  } | null>(null)

  // Filter apps based on search
  const filteredApps = useMemo(() => {
    if (!appSearch) return apps
    return apps.filter((app) =>
      app.app.toLowerCase().includes(appSearch.toLowerCase())
    )
  }, [apps, appSearch])

  // Calculate metrics using LTV-based model
  const calculateCashflow = useCallback(() => {
    const data = []
    const dailyUsers = kpis.monthlyInstalls / 30 // Convert monthly to daily
    const projectionDays = kpis.projectionDays
    const analysisHorizon = 365 // Always analyze 365 days for consistent messaging
    const maxDays = Math.max(projectionDays, analysisHorizon)

    // Calculate retention curve parameters ONCE
    const retentionParams = calculateRetentionCurveParams(
      kpis.day1Retention,
      kpis.day7Retention,
      kpis.day30Retention
    )

    let cumulativeSpend = calculatedMetrics?.totalSpend || 0 // Start with existing 30-day spend as baseline
    let cumulativeRevenue = calculatedMetrics?.totalRevenue || 0 // Start with existing 30-day revenue as baseline
    let firstPositiveDay = -1
    let firstPositiveDay365 = -1 // Track break-even within 365 days for analysis
    let lastProfitableDay365 = -1 // Track last day profit was positive within 365 days

    // Check if already profitable from existing business
    const initialProfit = cumulativeRevenue - cumulativeSpend
    if (initialProfit > 0) {
      firstPositiveDay = 0 // Already profitable from day 0
      firstPositiveDay365 = 0
      lastProfitableDay365 = 0 // Update as we go through the loop
    }

    // Store cohorts for LTV calculation (track by day)
    const cohorts: { users: number; acquisitionDay: number }[] = []

    for (let day = 0; day <= maxDays; day++) {
      // Calculate spend for this day
      const dailySpend = day === 0 ? 0 : kpis.monthlyUASpend / 30 // Convert monthly spend to daily
      cumulativeSpend += dailySpend

      // Add new cohort (except day 0)
      if (day > 0) {
        cohorts.push({ users: dailyUsers, acquisitionDay: day })
      }

      // Calculate revenue from all cohorts using LTV model
      let dailyRevenue = 0
      let totalActiveUsers = 0

      // Add baseline revenue from existing DAU (day 0 represents current performance)
      if (day === 0 && calculatedMetrics) {
        // Use current performance data for day 0 baseline
        dailyRevenue = calculatedMetrics.totalRevenue / 30 // Convert monthly to daily
        totalActiveUsers = calculatedMetrics.effectiveDAU
      } else {
        // For projection days, calculate revenue from all user cohorts
        cohorts.forEach(cohort => {
          const cohortAgeDays = day - cohort.acquisitionDay

          // Only count cohorts that have been acquired (cohortAgeDays >= 0)
          // Remove the projectionDays check - all cohorts should be counted regardless of age
          if (cohortAgeDays >= 0) {
            // Use piecewise exponential retention curve
            const retentionRate = calculateRetentionForDay(
              cohortAgeDays,
              retentionParams,
              kpis.day1Retention,
              kpis.day7Retention,
              kpis.day30Retention
            )

            const activeUsersInCohort = cohort.users * retentionRate
            totalActiveUsers += activeUsersInCohort

            // Use LTV-based revenue calculation for projections
            const dailyLTVContribution = kpis.userLTV / 365 // Use 365 days (LTV period), not projectionDays
            const cohortRevenue = activeUsersInCohort * dailyLTVContribution

            dailyRevenue += cohortRevenue
          }
        })
      }

      dailyRevenue = isFinite(dailyRevenue) ? dailyRevenue : 0
      cumulativeRevenue += dailyRevenue

      // Calculate profit
      const profit = cumulativeRevenue - cumulativeSpend
      const roi = cumulativeSpend > 0 ? cumulativeRevenue / cumulativeSpend : 0

      if (profit > 0 && firstPositiveDay === -1) {
        firstPositiveDay = day
      }

      if (day <= 365) {
        if (profit > 0 && firstPositiveDay365 === -1) {
          firstPositiveDay365 = day
        }
        if (profit > 0) {
          lastProfitableDay365 = day // Keep updating as long as profitable
        }
      }

      // Calculate retention percentage for this day using CURRENT KPI values
      let retentionPercentage = 100 // Start at 100%
      
      if (day === 0) {
        retentionPercentage = 100 // Day 0 is always 100%
      } else {
        // Calculate retention based on the exact number of days since install
        if (day === 1) {
          // Day 1 retention
          retentionPercentage = kpis.day1Retention
        } else if (day <= 7) {
          // Linear interpolation between D1 and D7
          const ratio = (day - 1) / 6
          retentionPercentage = kpis.day1Retention * (1 - ratio) + kpis.day7Retention * ratio
        } else if (day <= 30) {
          // Linear interpolation between D7 and D30
          const ratio = (day - 7) / 23
          retentionPercentage = kpis.day7Retention * (1 - ratio) + kpis.day30Retention * ratio
        } else {
          // Exponential decay after D30
          const daysAfter30 = day - 30
          retentionPercentage = kpis.day30Retention * Math.pow(0.9, daysAfter30 / 30)
        }
      }

      data.push({
        day,
        spend: Math.round(cumulativeSpend),
        revenue: Math.round(cumulativeRevenue),
        profit: Math.round(profit),
        roi: Math.round(roi * 10) / 10, // Round to 1 decimal place
        activeUsers: Math.round(totalActiveUsers),
        retention: Math.round(retentionPercentage * 10) / 10, // Round to 1 decimal place
      })
    }

    // Slice data to show only user-selected projection days for chart
    const displayData = data.slice(0, projectionDays + 1)
    setCashflowData(displayData)
    setBreakEvenMonth(firstPositiveDay) // For display purposes

    // Extract 365-day data for consistent analysis messaging
    const day365Data = data[365] || data[data.length - 1]
    const profit365 = day365Data?.profit || 0
    const roi365 = day365Data?.roi || 0
    const dailyBurnRate365 = Math.abs(profit365) / 365

    setAnalysis365({
      profit: profit365,
      roi: roi365,
      breakEvenDay: firstPositiveDay365,
      lastProfitableDay: lastProfitableDay365,
      dailyBurnRate: dailyBurnRate365
    })

    // Check if app is profitable based on FINAL profit of displayed period
    const finalProfit = displayData[displayData.length - 1]?.profit || 0
    const isProfitable = finalProfit > 0
    setIsPositive(isProfitable)
  }, [kpis, calculatedMetrics])

  // Load apps when organization slug changes
  useEffect(() => {
    if (orgSlug && organizations.length > 0) {
      // Find the organization by slug
      const org = organizations.find(o => o.slug === orgSlug)
      if (org) {
        loadApps(org.id)
      }
    }
  }, [orgSlug, organizations])

  // Load apps from API
  const loadApps = async (_organizationId: number) => {
    // Prevent duplicate calls when already loading
    if (isLoadingRef.current) {
      return
    }
    
    isLoadingRef.current = true
    setLoadingApps(true)
    setSelectedApp('All Apps') // Reset selection when org changes
    
    try {
      const appMetrics = await optimizerService.fetchAppMetrics()
      setApps(appMetrics)

      // If we have apps, select "All Apps" by default and fetch retention data
      if (appMetrics.length > 0) {
        const aggregated = optimizerService.calculateAggregatedMetrics(appMetrics)

        // Fetch retention data for "All Apps" on initial load
        try {
          const retentionData = await optimizerService.fetchRetentionData(undefined)
          setRetentionCache(prev => new Map(prev).set('All Apps', retentionData))
          const retentionValues = fitRetentionCurve(retentionData)
          updateRetentionSliders(retentionValues.d1, retentionValues.d7, retentionValues.d30)
          updateKPIsFromApp(aggregated, retentionValues)
        } catch (error) {
          console.error('Failed to load initial retention data:', error)
          updateKPIsFromApp(aggregated)
        }
      }
    } catch (error) {
      console.error('Failed to load apps:', error)
      toast.error('Failed to load app metrics')
    } finally {
      setLoadingApps(false)
      isLoadingRef.current = false
    }
  }

  // Update KPIs when an app is selected
  const updateKPIsFromApp = (
    appData: AppMetrics | null,
    retentionValues?: { d1: number; d7: number; d30: number }
  ) => {
    if (!appData) {
      // Reset to zeros if no app data
      setKPIs(prev => ({
        monthlyUASpend: 0,
        monthlyInstalls: 0,
        cpi: 0,
        day1Retention: retentionValues?.d1 ?? 40,
        day7Retention: retentionValues?.d7 ?? 20,
        day30Retention: retentionValues?.d30 ?? 10,
        effectiveDAU: 0,
        userLTV: 0,
        eCPM: 0,
        adImpressionRate: 0,
        monthlyAdRevenue: 0,
        monthlyIAPRevenue: 0,
        monthlyTotalRevenue: 0,
        projectionDays: prev.projectionDays, // Preserve time horizon
      }))
      setCalculatedMetrics(null)
      return
    }

    // Calculate initial eCPM from real data first
    const calculatedECPM = (appData.ad_impressions || 0) > 0
      ? (appData.ad_revenue || 0) / ((appData.ad_impressions || 0) / 1000)
      : 0 // Use 0 as fallback instead of 1.5

    // Calculate derived metrics from real data using the calculated eCPM
    const derivedMetrics = calculateDerivedMetrics(appData, calculatedECPM)
    setCalculatedMetrics(derivedMetrics)

    // Estimate LTV from current performance data using dynamic time horizon
    // Use passed retention values or fall back to current KPI values
    const d1 = retentionValues?.d1 ?? kpis.day1Retention
    const d7 = retentionValues?.d7 ?? kpis.day7Retention
    const d30 = retentionValues?.d30 ?? kpis.day30Retention

    const timeHorizonMonths = Math.max(1, Math.ceil(kpis.projectionDays / 30))
    const estimatedLTV = estimateLTVFromData(
      derivedMetrics.arpdau,
      d1,
      d7,
      d30,
      timeHorizonMonths // Use dynamic time horizon from projection days
    )

    setKPIs(prev => ({
      monthlyUASpend: appData.ua_spent ?? 0,
      monthlyInstalls: appData.installs ?? 0,
      cpi: appData.cpi ?? 0,
      day1Retention: d1, // Use passed values or preserve existing
      day7Retention: d7,
      day30Retention: d30,
      effectiveDAU: derivedMetrics.effectiveDAU, // Use calculated effective DAU
      userLTV: estimatedLTV, // Calculated from real data
      eCPM: calculatedECPM, // Use calculated eCPM from real data
      adImpressionRate: 3, // Default as we don't have this in the API
      monthlyAdRevenue: appData.ad_revenue ?? 0, // Use actual ad revenue
      monthlyIAPRevenue: appData.iap_revenue ?? 0, // Use actual IAP revenue
      monthlyTotalRevenue: (appData.ad_revenue ?? 0) + (appData.iap_revenue ?? 0), // Combined revenue
      projectionDays: prev.projectionDays, // Preserve current time horizon
    }))
  }

  // Update retention slider values
  const updateRetentionSliders = (d1: number, d7: number, d30: number) => {
    setKPIs(prev => ({
      ...prev,
      day1Retention: d1,
      day7Retention: d7,
      day30Retention: d30,
    }))
  }

  // Handle app selection
  const handleAppChange = async (appName: string) => {
    setSelectedApp(appName)

    // 1. Load basic metrics
    const appData = appName === 'All Apps'
      ? optimizerService.calculateAggregatedMetrics(apps)
      : apps.find(app => app.app === appName) || null

    // 2. Check retention cache and query if needed
    let retentionValues = { d1: kpis.day1Retention, d7: kpis.day7Retention, d30: kpis.day30Retention }

    if (!retentionCache.has(appName)) {
      setLoadingRetention(true)
      try {
        const retentionData = await optimizerService.fetchRetentionData(
          appName === 'All Apps' ? undefined : appName
        )
        setRetentionCache(prev => new Map(prev).set(appName, retentionData))

        // 3. Fit curve and get retention values
        retentionValues = fitRetentionCurve(retentionData)
        updateRetentionSliders(retentionValues.d1, retentionValues.d7, retentionValues.d30)
      } catch (error) {
        console.error('Failed to load retention data:', error)
        toast.warning('Using default retention values')
        retentionValues = { d1: 40, d7: 20, d30: 10 }
        updateRetentionSliders(40, 20, 10)
      } finally {
        setLoadingRetention(false)
      }
    } else {
      // Use cached data
      const cachedData = retentionCache.get(appName)!
      retentionValues = fitRetentionCurve(cachedData)
      updateRetentionSliders(retentionValues.d1, retentionValues.d7, retentionValues.d30)
    }

    // 4. Update other KPIs with the NEW retention values
    updateKPIsFromApp(appData, retentionValues)
  }

  // Handle CSV download - fetch CSV URL on-demand
  const handleDownloadCSV = async () => {
    const csvUrl = await optimizerService.fetchCsvUrl()
    if (csvUrl) {
      // Create a temporary link element to trigger download
      const link = document.createElement('a')
      link.href = csvUrl
      link.download = `app-metrics-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('CSV download started')
    }
  }

  // Get current app data for reset functionality
  const getCurrentAppData = (): AppMetrics | null => {
    if (selectedApp === 'All Apps') {
      return optimizerService.calculateAggregatedMetrics(apps)
    } else {
      return apps.find(app => app.app === selectedApp) || null
    }
  }

  // Reset handlers - restore to loaded app data
  const resetUserAcquisition = () => {
    const appData = getCurrentAppData()
    if (appData) {
      setKPIs(prev => ({
        ...prev,
        monthlyUASpend: appData.ua_spent ?? 0,
        monthlyInstalls: appData.installs ?? 0,
        cpi: appData.cpi ?? 0,
      }))
    }
  }

  const resetRetention = () => {
    const appData = getCurrentAppData()
    if (appData) {
      // Recalculate eCPM and derived metrics from app data
      const calculatedECPM = (appData.ad_impressions || 0) > 0
        ? (appData.ad_revenue || 0) / ((appData.ad_impressions || 0) / 1000)
        : 0

      const derivedMetrics = calculateDerivedMetrics(appData, calculatedECPM)

      // Get retention values from cache or use defaults
      const cachedData = retentionCache.get(selectedApp)
      if (cachedData) {
        const { d1, d7, d30 } = fitRetentionCurve(cachedData)
        updateRetentionSliders(d1, d7, d30)
      } else {
        updateRetentionSliders(40, 20, 10)
      }

      setKPIs(prev => ({
        ...prev,
        effectiveDAU: derivedMetrics.effectiveDAU,
      }))

      // Update calculated metrics state
      setCalculatedMetrics(derivedMetrics)
    }
  }

  const resetMonetization = () => {
    const appData = getCurrentAppData()
    if (appData) {
      const calculatedECPM = (appData.ad_impressions || 0) > 0
        ? (appData.ad_revenue || 0) / ((appData.ad_impressions || 0) / 1000)
        : 0

      const timeHorizonMonths = Math.max(1, Math.ceil(kpis.projectionDays / 30))
      const derivedMetrics = calculateDerivedMetrics(appData, calculatedECPM)
      const estimatedLTV = estimateLTVFromData(
        derivedMetrics.arpdau,
        kpis.day1Retention, // Use current retention values
        kpis.day7Retention,
        kpis.day30Retention,
        timeHorizonMonths
      )

      setKPIs(prev => ({
        ...prev,
        monthlyAdRevenue: appData.ad_revenue ?? 0,
        monthlyIAPRevenue: appData.iap_revenue ?? 0,
        monthlyTotalRevenue: (appData.ad_revenue ?? 0) + (appData.iap_revenue ?? 0),
        eCPM: calculatedECPM,
        userLTV: estimatedLTV,
      }))

      // Update calculated metrics state
      setCalculatedMetrics(derivedMetrics)
    }
  }

  useEffect(() => {
    calculateCashflow()
  }, [kpis, calculatedMetrics, calculateCashflow])

  const updateKPI = (key: keyof KPIValues, value: number) => {
    setKPIs(prev => {
      const newKPIs = { ...prev, [key]: value }

      // Keep UA spend, installs, and CPI in sync
      if (key === 'monthlyUASpend' && newKPIs.cpi > 0) {
        newKPIs.monthlyInstalls = Math.round(value / newKPIs.cpi)
      } else if (key === 'monthlyInstalls' && newKPIs.cpi > 0) {
        newKPIs.monthlyUASpend = Math.round(value * newKPIs.cpi)
      } else if (key === 'cpi' && value > 0) {
        newKPIs.monthlyUASpend = Math.round(newKPIs.monthlyInstalls * value)
      }

      // CASCADE: UA → Retention → Monetization
      // When UA Spend, Installs, CPI, or Retention changes → recalculate projected DAU
      const uaKeys: (keyof KPIValues)[] = ['monthlyUASpend', 'monthlyInstalls', 'cpi']
      const retentionKeys: (keyof KPIValues)[] = ['day1Retention', 'day7Retention', 'day30Retention']

      if (uaKeys.includes(key) || retentionKeys.includes(key)) {
        // Calculate steady-state DAU from install rate and retention curve
        const dailyInstalls = newKPIs.monthlyInstalls / 30
        const projectedDAU = calculateSteadyStateDAU(
          dailyInstalls,
          newKPIs.day1Retention,
          newKPIs.day7Retention,
          newKPIs.day30Retention
        )

        // Update DAU to projected steady-state value
        newKPIs.effectiveDAU = Math.ceil(projectedDAU)

        // Keep ARPDAU constant, recalculate Revenue from new DAU
        if (calculatedMetrics && calculatedMetrics.arpdau > 0) {
          const baselineARPDAU = calculatedMetrics.arpdau
          const newMonthlyRevenue = Math.round(baselineARPDAU * newKPIs.effectiveDAU * 30)

          // Split revenue proportionally between Ad and IAP
          const currentTotal = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
          if (currentTotal > 0) {
            const adRatio = newKPIs.monthlyAdRevenue / currentTotal
            const iapRatio = newKPIs.monthlyIAPRevenue / currentTotal
            newKPIs.monthlyAdRevenue = Math.round(newMonthlyRevenue * adRatio)
            newKPIs.monthlyIAPRevenue = Math.round(newMonthlyRevenue * iapRatio)
          } else {
            // Default split: 70% Ad, 30% IAP
            newKPIs.monthlyAdRevenue = Math.round(newMonthlyRevenue * 0.7)
            newKPIs.monthlyIAPRevenue = Math.round(newMonthlyRevenue * 0.3)
          }
          newKPIs.monthlyTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue

          // Update calculatedMetrics with new DAU and revenue
          setCalculatedMetrics({
            ...calculatedMetrics,
            effectiveDAU: newKPIs.effectiveDAU,
            totalRevenue: newKPIs.monthlyTotalRevenue,
            arpdau: baselineARPDAU, // Keep ARPDAU constant
          })
        }
      }

      // RETENTION LOGIC: Cascading zeros (no users can survive to later stages)
      if (key === 'day1Retention' && value === 0) {
        newKPIs.day7Retention = 0
        newKPIs.day30Retention = 0
      }
      if (key === 'day7Retention' && value === 0) {
        newKPIs.day30Retention = 0
      }

      // CASCADE: DAU → Revenue (only when DAU slider is directly changed)
      // DAU is a "present" metric, Retention/Installs affect "future" projections
      if (key === 'effectiveDAU' && calculatedMetrics && prev.effectiveDAU > 0) {
        const scaleFactor = newKPIs.effectiveDAU / prev.effectiveDAU
        newKPIs.monthlyAdRevenue = Math.round(newKPIs.monthlyAdRevenue * scaleFactor)
        newKPIs.monthlyIAPRevenue = Math.round(newKPIs.monthlyIAPRevenue * scaleFactor)
        newKPIs.monthlyTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue

        // Update calculatedMetrics to reflect the revenue change
        const newTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
        const newARPDAU = newKPIs.effectiveDAU > 0
          ? newTotalRevenue / newKPIs.effectiveDAU / 30
          : 0

        setCalculatedMetrics({
          ...calculatedMetrics,
          totalRevenue: newTotalRevenue,
          arpdau: newARPDAU,
          effectiveDAU: newKPIs.effectiveDAU,
        })
      }
      
      // Handle LTV changes - reverse-calculate required monthly revenue
      if (key === 'userLTV' && calculatedMetrics) {
        const currentEffectiveDAU = calculatedMetrics.effectiveDAU
        
        if (currentEffectiveDAU > 0) {
          // Calculate what ARPDAU is needed for the target LTV using current retention rates
          const timeHorizonMonths = Math.max(1, Math.ceil(newKPIs.projectionDays / 30))
          const targetLTV = value
          
          // Use the actual LTV calculation function to find required ARPDAU
          // Try different ARPDAU values until we find one that produces the target LTV
          let requiredARPDAU = 0
          let bestARPDAU = 0
          let minDifference = Infinity
          
          // Binary search for the correct ARPDAU
          for (let testARPDAU = 0.01; testARPDAU <= 10; testARPDAU += 0.01) {
            const calculatedLTV = estimateLTVFromData(
              testARPDAU,
              newKPIs.day1Retention,
              newKPIs.day7Retention,
              newKPIs.day30Retention,
              timeHorizonMonths
            )
            
            const difference = Math.abs(calculatedLTV - targetLTV)
            if (difference < minDifference) {
              minDifference = difference
              bestARPDAU = testARPDAU
            }
            
            // Early exit if we found exact match
            if (difference < 0.01) {
              requiredARPDAU = testARPDAU
              break
            }
          }
          
          requiredARPDAU = requiredARPDAU || bestARPDAU
          
          if (requiredARPDAU > 0) {
            // Convert ARPDAU back to monthly revenue: ARPDAU = monthlyRevenue / effectiveDAU / 30
            const requiredMonthlyRevenue = Math.round(requiredARPDAU * currentEffectiveDAU * 30)
            
            // Update total revenue and split proportionally
            const currentTotal = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
            if (currentTotal > 0) {
              // Maintain current proportions
              const adRatio = newKPIs.monthlyAdRevenue / currentTotal
              const iapRatio = newKPIs.monthlyIAPRevenue / currentTotal
              newKPIs.monthlyAdRevenue = Math.round(requiredMonthlyRevenue * adRatio)
              newKPIs.monthlyIAPRevenue = Math.round(requiredMonthlyRevenue * iapRatio)
            } else {
              // Default split: 80% Ad, 20% IAP
              newKPIs.monthlyAdRevenue = Math.round(requiredMonthlyRevenue * 0.8)
              newKPIs.monthlyIAPRevenue = Math.round(requiredMonthlyRevenue * 0.2)
            }
            newKPIs.monthlyTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
          }
        }
      }
      
      // Handle total revenue changes - split proportionally between IAP and Ad
      if (key === 'monthlyTotalRevenue') {
        const currentTotal = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
        if (currentTotal > 0) {
          // Maintain current proportions
          const adRatio = newKPIs.monthlyAdRevenue / currentTotal
          const iapRatio = newKPIs.monthlyIAPRevenue / currentTotal
          newKPIs.monthlyAdRevenue = Math.round(value * adRatio)
          newKPIs.monthlyIAPRevenue = Math.round(value * iapRatio)
        } else {
          // Default split: 80% Ad, 20% IAP (based on typical mobile app monetization)
          newKPIs.monthlyAdRevenue = Math.round(value * 0.8)
          newKPIs.monthlyIAPRevenue = Math.round(value * 0.2)
        }
      }
      
      // Update total revenue when individual revenues change
      if (key === 'monthlyAdRevenue' || key === 'monthlyIAPRevenue') {
        newKPIs.monthlyTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
      }
      
      // Update ad revenue when eCPM changes
      if (key === 'eCPM' && calculatedMetrics) {
        // Find current app data to get impressions
        const currentApp = selectedApp === 'All Apps' 
          ? optimizerService.calculateAggregatedMetrics(apps)
          : apps.find(app => app.app === selectedApp)
        
        if (currentApp && (currentApp.ad_impressions || 0) > 0) {
          // Recalculate ad revenue: new_eCPM × (impressions / 1000)
          const newAdRevenue = value * ((currentApp.ad_impressions || 0) / 1000)
          newKPIs.monthlyAdRevenue = Math.round(newAdRevenue)
          // Update total revenue when ad revenue changes due to eCPM
          newKPIs.monthlyTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
        }
      }
      
      // Recalculate LTV estimate when relevant KPIs change
      // Note: projectionDays removed - LTV is now calculated over fixed 365-day period
      const ltvRelevantKeys: (keyof KPIValues)[] = [
        'eCPM', 'day1Retention',
        'day7Retention', 'day30Retention', 'monthlyAdRevenue', 'monthlyIAPRevenue', 'monthlyTotalRevenue',
        'effectiveDAU' // Add effectiveDAU so LTV updates when DAU changes
      ]
      
      if (ltvRelevantKeys.includes(key) && calculatedMetrics) {
        // Update calculated metrics when eCPM, revenue, or DAU changes
        let updatedMetrics = calculatedMetrics
        if (key === 'eCPM' || key === 'monthlyAdRevenue' || key === 'monthlyIAPRevenue' || key === 'monthlyTotalRevenue' || key === 'effectiveDAU') {
          // Update the metrics to reflect slider changes
          const newTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
          const effectiveDAUToUse = key === 'effectiveDAU' ? newKPIs.effectiveDAU : calculatedMetrics.effectiveDAU
          const newARPDAU = effectiveDAUToUse > 0 
            ? newTotalRevenue / effectiveDAUToUse / 30 // Daily ARPDAU
            : 0
          const newECPM = newKPIs.eCPM // Use the user-adjusted eCPM
          
          updatedMetrics = {
            ...calculatedMetrics,
            totalRevenue: newTotalRevenue,
            totalSpend: calculatedMetrics.totalSpend, // Keep existing spend baseline
            arpdau: newARPDAU,
            eCPM: newECPM,
            effectiveDAU: effectiveDAUToUse,
            isDAUEstimated: calculatedMetrics.isDAUEstimated
          }
          setCalculatedMetrics(updatedMetrics)
        }
        
        // Calculate new LTV estimate using updated values
        const timeHorizonMonths = Math.max(1, Math.ceil(newKPIs.projectionDays / 30))
        
        // Calculate fresh ARPDAU using current revenue values (always use fresh values)
        const freshTotalRevenue = newKPIs.monthlyAdRevenue + newKPIs.monthlyIAPRevenue
        const currentEffectiveDAU = key === 'effectiveDAU' ? newKPIs.effectiveDAU : updatedMetrics.effectiveDAU
        const freshARPDAU = currentEffectiveDAU > 0 
          ? freshTotalRevenue / currentEffectiveDAU / 30 // Daily ARPDAU
          : updatedMetrics.arpdau // Fallback to existing if no DAU
        
        const estimatedLTV = estimateLTVFromData(
          freshARPDAU, // Use fresh ARPDAU that reflects revenue changes
          newKPIs.day1Retention,
          newKPIs.day7Retention,
          newKPIs.day30Retention,
          timeHorizonMonths
        )
        
        // Only update LTV if it's not being manually changed by user
        if (key !== 'userLTV') {
          newKPIs.userLTV = estimatedLTV
        }
      }
      
      return newKPIs
    })
  }

  const formatCurrency = (value: number) => {
    if (!isFinite(value) || isNaN(value)) return '$0'
    
    const absValue = Math.abs(value)
    if (absValue >= 1e6) return `$${(value / 1e6).toFixed(1)}M`
    if (absValue >= 1e3) return `$${(value / 1e3).toFixed(1)}k`
    return `$${value.toFixed(0)}`
  }

  const finalData = cashflowData[cashflowData.length - 1] || {}
  const finalROI = finalData.roi || 0
  const finalProfit = finalData.profit || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Will Your App Make Money?</h1>
          <p className="text-muted-foreground">
            Model different scenarios to optimize your app strategy.
          </p>
        </div>
      </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Results - Shows first (top on mobile, left on desktop) */}
          <div className="space-y-6">
            {/* Summary */}
            <Card className={loadingApps || apps.length === 0 ? 'border-gray-300' : (analysis365 && analysis365.profit > 0 ? 'border-green-500' : 'border-red-500')}>
              <div className="grid grid-cols-4 gap-4 p-6">
                {/* Row 1: Title + Description (3 cols) + Buttons (1 col) */}
                <div className="col-span-3 space-y-1.5">
                  <CardTitle className={loadingApps || apps.length === 0 ? 'text-gray-500' : (analysis365 && analysis365.profit > 0 ? 'text-green-600' : 'text-red-600')}>
                    {loadingApps || apps.length === 0 ? 'No data available' : `${selectedApp}: ${analysis365 && analysis365.profit > 0 ? 'Profitable' : 'Not Profitable'}`}
                  </CardTitle>
                  <CardDescription>
                    {loadingApps || apps.length === 0
                      ? 'Load app data to see profitability analysis'
                      : kpis.projectionDays === 0
                        ? (isPositive
                            ? `Current 30-day performance: ${formatCurrency(finalProfit)} net profit`
                            : `Current 30-day performance: ${formatCurrency(Math.abs(finalProfit))} net loss`
                          )
                        : !analysis365
                          ? 'Calculating analysis...'
                          : (() => {
                              const { profit: profit365, lastProfitableDay: lastProfit, dailyBurnRate: burnRate365 } = analysis365
                              const isProfitable365 = profit365 > 0

                              // 1. Forever profitable
                              if (isProfitable365) {
                                return 'Profitable from day 1'
                              }

                              // 2. Profitable but trending down
                              if (lastProfit >= 0) {
                                return `Profitable for ${lastProfit} days then loses ${formatCurrency(burnRate365)}/day.`
                              }

                              // 3. Never profitable
                              return `Not profitable from day 1 losing ${formatCurrency(burnRate365)}/day.`
                            })()
                    }
                  </CardDescription>
                </div>
                <div className="flex items-start gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleDownloadCSV}
                    disabled={!optimizerService.hasRequestParams()}
                    title="Download CSV"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Popover open={appSelectorOpen} onOpenChange={setAppSelectorOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={loadingApps || apps.length === 0}
                        title="Select App"
                      >
                        {loadingApps ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Smartphone className="h-4 w-4" />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-64 p-0">
                      <div className="flex items-center border-b px-3 py-2">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                          type="text"
                          placeholder="Search apps..."
                          value={appSearch}
                          onChange={(e) => setAppSearch(e.target.value)}
                          className="h-8 w-full border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0"
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto p-1">
                        {apps.length > 0 && (
                          <div
                            className="relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                            onClick={() => {
                              handleAppChange('All Apps')
                              setAppSelectorOpen(false)
                              setAppSearch('')
                            }}
                          >
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              {selectedApp === 'All Apps' && <Check className="h-4 w-4" />}
                            </span>
                            All Apps
                          </div>
                        )}
                        {filteredApps.map((app) => (
                          <div
                            key={app.app}
                            className="relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                            onClick={() => {
                              handleAppChange(app.app)
                              setAppSelectorOpen(false)
                              setAppSearch('')
                            }}
                          >
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              {selectedApp === app.app && <Check className="h-4 w-4" />}
                            </span>
                            <span className="truncate">{app.app}</span>
                          </div>
                        ))}
                        {apps.length === 0 && !loadingApps && (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No apps found for this organization
                          </div>
                        )}
                        {filteredApps.length === 0 && apps.length > 0 && (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No apps found matching "{appSearch}"
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Row 2: 4 Metrics */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Investment</p>
                  <p className="text-2xl font-bold text-right">{formatCurrency(finalData.spend)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold text-right">{formatCurrency(finalData.revenue)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Net Profit/Loss</p>
                  <p className={`text-2xl font-bold text-right ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(finalProfit)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Return</p>
                  <p className={`text-2xl font-bold text-right ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {finalROI.toFixed(1)}x
                  </p>
                </div>
              </div>
            </Card>

            {/* Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cumulative Cashflow Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={cashflowData} margin={{ top: 40, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.8} />
                        <stop offset="50%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="50%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.8} />
                      </linearGradient>
                      <linearGradient id="profitGradientPositive" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="profitGradientNegative" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.1} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(v) => `D${v}`}
                      ticks={[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360]}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      yAxisId="left"
                      label={{ value: 'Total Users', position: 'insideTop', offset: -25, style: { fontSize: 12, fill: '#666' } }}
                      tickFormatter={(value) => {
                        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
                        return value.toString()
                      }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      label={{ value: 'Profit/Loss', position: 'insideTop', offset: -25, style: { fontSize: 12, fill: '#666' } }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        if (name === 'activeUsers') {
                          return [value.toLocaleString(), 'Total Users']
                        }
                        return [formatCurrency(value), name === 'profit' ? 'Profit' : name]
                      }}
                      labelFormatter={(label) => `Day ${label}`}
                    />
                    <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" yAxisId="right" />
                    <Area
                      type="monotone"
                      dataKey="profit"
                      stroke={isPositive ? '#22c55e' : '#ef4444'}
                      strokeWidth={2}
                      fill={isPositive ? 'url(#profitGradientPositive)' : 'url(#profitGradientNegative)'}
                      yAxisId="right"
                    />
                    <Line
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={false}
                      yAxisId="left"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Time Horizon */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Time Horizon</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="days">Projection Period - {kpis.projectionDays} days</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="365"
                      value={kpis.projectionDays}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0
                        const clampedValue = Math.min(Math.max(value, 0), 365)
                        updateKPI('projectionDays', clampedValue)
                      }}
                      className="w-20 h-8 text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateKPI('projectionDays', 180)}
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <Slider
                  id="days"
                  min={0}
                  max={365}
                  step={1}
                  value={[kpis.projectionDays]}
                  onValueChange={(value) => updateKPI('projectionDays', value[0])}
                />
              </CardContent>
            </Card>

            {/* How it Works */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How the Calculator Works</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">Data Period:</strong> The loaded data represents a rolling 30-day window (d-30 to d-1), capturing the most recent historical performance. Day 0 shows this baseline data, while future days project performance based on your adjusted parameters and the Time Horizon setting.
                </p>
                <p>
                  This calculator keeps a running tally of the most common expenses and revenues associated with running a mobile app.
                </p>
                <p>
                  <strong className="text-foreground">User Acquisition:</strong> The monthly amount spent on advertising and the cost per install determine how many new users you acquire each month.
                </p>
                <p>
                  <strong className="text-foreground">Retention:</strong> Not all users who install your app will continue using it. The retention rates show what percentage of users remain active after 1, 7, and 30 days.
                </p>
                <p>
                  <strong className="text-foreground">Monetization:</strong> Revenue is modeled using LTV (Lifetime Value) which represents the total expected revenue from a user over their entire lifecycle. This provides more accurate long-term projections than daily metrics alone.
                </p>
                <p>
                  <strong className="text-foreground">LTV Calculation:</strong> LTV = ARPDAU × User Lifespan, where ARPDAU (Average Revenue Per Daily Active User) = Total Revenue ÷ DAU ÷ 30 days, and User Lifespan is calculated using retention curves (D1, D7, D30) with exponential decay over the selected time horizon.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Controls - Shows second (bottom on mobile, right on desktop) */}
          <div className="space-y-6">

            {/* User Acquisition */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">User Acquisition</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={resetUserAcquisition}
                    title="Reset to loaded data"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="ua-spend">Monthly UA Spend - {formatCurrency(kpis.monthlyUASpend)}</Label>
                    <Input
                      type="number"
                      step="1"
                      value={Math.round(kpis.monthlyUASpend)}
                      onChange={(e) => updateKPI('monthlyUASpend', parseFloat(e.target.value) || 0)}
                      className="w-24 h-8 text-sm"
                    />
                  </div>
                  <Slider
                    id="ua-spend"
                    min={0}
                    max={200000}
                    step={5000}
                    value={[kpis.monthlyUASpend]}
                    onValueChange={(value) => updateKPI('monthlyUASpend', value[0])}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="installs">Monthly Installs - {formatCurrency(kpis.monthlyInstalls).replace('$', '')}</Label>
                  <Slider
                    id="installs"
                    min={0}
                    max={100000}
                    step={1000}
                    value={[kpis.monthlyInstalls]}
                    onValueChange={(value) => updateKPI('monthlyInstalls', value[0])}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="cpi">CPI - ${kpis.cpi.toFixed(2)}</Label>
                  <Slider
                    id="cpi"
                    min={0.1}
                    max={10}
                    step={0.1}
                    value={[kpis.cpi]}
                    onValueChange={(value) => updateKPI('cpi', value[0])}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Retention */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Retention</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={resetRetention}
                    title="Reset to loaded data"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label htmlFor="effective-dau">
                    DAU - {formatCurrency(kpis.effectiveDAU).replace('$', '')}
                  </Label>
                  <Slider
                    id="effective-dau"
                    min={100}
                    max={100000}
                    step={100}
                    value={[kpis.effectiveDAU]}
                    onValueChange={(value) => updateKPI('effectiveDAU', value[0])}
                    variant={calculatedMetrics?.isDAUEstimated ? "estimated" : "default"}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="d1">Day 1 Retention - {kpis.day1Retention.toFixed(1)}%</Label>
                  <Slider
                    id="d1"
                    min={0}
                    max={100}
                    step={1}
                    value={[kpis.day1Retention]}
                    onValueChange={(value) => updateKPI('day1Retention', value[0])}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="d7">Day 7 Retention - {kpis.day7Retention.toFixed(1)}%</Label>
                  <Slider
                    id="d7"
                    min={0}
                    max={100}
                    step={1}
                    value={[kpis.day7Retention]}
                    onValueChange={(value) => updateKPI('day7Retention', value[0])}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="d30">Day 30 Retention - {kpis.day30Retention.toFixed(1)}%</Label>
                  <Slider
                    id="d30"
                    min={0}
                    max={100}
                    step={1}
                    value={[kpis.day30Retention]}
                    onValueChange={(value) => updateKPI('day30Retention', value[0])}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Monetization */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Monetization</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={resetMonetization}
                    title="Reset to loaded data"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Monthly Total Revenue - Consolidated Slider */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="monthly-total-revenue">Monthly Total Revenue - {formatCurrency(kpis.monthlyTotalRevenue)}</Label>
                    <Input
                      type="number"
                      step="1"
                      value={Math.round(kpis.monthlyTotalRevenue)}
                      onChange={(e) => updateKPI('monthlyTotalRevenue', parseFloat(e.target.value) || 0)}
                      className="w-24 h-8 text-sm"
                    />
                  </div>
                  <Slider
                    id="monthly-total-revenue"
                    min={0}
                    max={70000}
                    step={100}
                    value={[kpis.monthlyTotalRevenue]}
                    onValueChange={(value) => updateKPI('monthlyTotalRevenue', value[0])}
                  />
                  {/* Revenue breakdown display */}
                  <div className="text-xs text-muted-foreground mt-2">
                    Ad Revenue: {formatCurrency(kpis.monthlyAdRevenue)} • IAP Revenue: {formatCurrency(kpis.monthlyIAPRevenue)}
                  </div>
                </div>

                {/* eCPM Simulation Slider */}
                <div className="space-y-3">
                  <Label htmlFor="ecpm">
                    eCPM - ${kpis.eCPM.toFixed(2)}
                  </Label>
                  <Slider
                    id="ecpm"
                    min={0.1}
                    max={10.0}
                    step={0.1}
                    value={[kpis.eCPM]}
                    onValueChange={(value) => updateKPI('eCPM', value[0])}
                  />
                </div>

                {/* 4th: LTV Slider */}
                <div className="space-y-3">
                  <Label htmlFor="ltv">
                    LTV - ${kpis.userLTV.toFixed(2)}
                  </Label>
                  <Slider
                    id="ltv"
                    min={1}
                    max={200}
                    step={1}
                    value={[kpis.userLTV]}
                    onValueChange={(value) => updateKPI('userLTV', value[0])}
                    variant="estimated"
                  />
                </div>

              </CardContent>
            </Card>
          </div>
      </div>
    </div>
  )
}